import React, { useEffect, useMemo, useState } from 'react';
import type { AuthenticatedContractorModel } from 'entities/profile';
import { useGetContractor, useGetUser } from 'entities/profile';
import {
  getSingleOverbudgetCampaign,
  ContractorCampaignsPageFilter,
  useGetCampaignContractorFilter,
  useGetCampaigns,
} from 'entities/campaign';
import { useTitleStoreActions } from 'shared/lib/store';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { ButtonGroups } from 'shared/ui/ButtonGroups';
import { usePaginationReset, useUrlResolver } from 'shared/lib/url';
import { Button } from 'shared/ui/Button';
import { PageError } from 'shared/ui/PageError';
import { Loader } from 'shared/ui/Loader';
import { InfoBox } from 'shared/ui/InfoBox';
import type { ContractorCampaignModel } from 'entities/campaign';
import { NoCampaignsCharacter } from 'shared/ui/Icons';
import { getPageUiStates } from 'shared/lib/api';
import { logError } from 'shared/lib/log-errors';
import { MODAL_ID, SideModalFromUrlParam } from 'shared/lib/hooks';
import { CreateCampaignWizard } from 'features/create-campaign';
import { ContractorCampaignsOverbudget, ContractorCampaignsViewAll } from 'widgets/ContractorCampaigns';
import { OnboardingCampaign, useGetOnboardingStatus } from 'widgets/CampaignOnboarding';
import { useFeatureIsOn, FEATURES } from 'shared/lib/growthbook';
import { CampaignEditModal } from 'widgets/EditCampaign';
import { HolidayScheduleModal } from 'widgets/HolidaySchedule';
import { getKeys } from 'shared/lib/object';
import { useLocation } from 'react-router-dom';

const TAB_ID_PARAM = 'tabId';

export const CAMPAIGN_CREATION_QUERY_KEY = 'add-campaign';

enum Tabs {
  ViewAll,
  Overbudget,
}

export const HOLYDAY_SCHEDULE_QUERY_KEY = 'holiday_schedule';

export function ContractorCampaigns() {
  const location = useLocation();
  const personQuery = useGetUser<AuthenticatedContractorModel>();
  const { addParam, getParam } = useUrlResolver();
  const currentTab = useMemo(() => {
    const currentTab = getParam(TAB_ID_PARAM) ?? Tabs.ViewAll;

    return Number(currentTab);
  }, [location]);
  const { setTitle, removeTitle } = useTitleStoreActions();
  const IS_ONBOARDING_ENABLE = useFeatureIsOn(FEATURES.ONBOARDING_ENABLED);
  const { removeParam } = useUrlResolver();

  const [isCampaignCreation, setIsCampaignCreation] = useState(false);
  const filtersFromURL = useGetCampaignContractorFilter();

  const getCampaignsQuery = useGetCampaigns<ContractorCampaignModel>({ urlFilters: filtersFromURL });
  const userQuery = useGetContractor();

  const singleOverbudgetCampaign = getSingleOverbudgetCampaign();
  const getOnboardingStatusQuery = useGetOnboardingStatus();

  const onboardingData = getOnboardingStatusQuery.data?.data;

  const isOnboarding = Boolean(onboardingData && !onboardingData.meta.completed);

  const pageStates = getPageUiStates([getCampaignsQuery, userQuery]);

  const totalOverbudgetCampaigns = singleOverbudgetCampaign.data?.meta.total;

  const resetPagination = usePaginationReset();

  const startCampaignCreation = () => {
    setIsCampaignCreation(true);
  };

  const onTabClick = (tab: Tabs) => {
    addParam(TAB_ID_PARAM, `${tab}`);

    getKeys(ContractorCampaignsPageFilter).forEach((key) => {
      removeParam(ContractorCampaignsPageFilter[key]);
    });

    resetPagination();
  };

  const onUpdateCampaignCompletion = () => {
    getCampaignsQuery.remove();
    getCampaignsQuery.refetch().catch(logError);
  };

  const onCreateCampaignCompletion = () => {
    getOnboardingStatusQuery.refetch().catch(logError);
    onUpdateCampaignCompletion();
    setIsCampaignCreation(false);
  };

  const openHolidaySchedule = () => addParam(HOLYDAY_SCHEDULE_QUERY_KEY, '1');

  const buttonGroupsItems = [
    {
      id: Tabs.ViewAll,
      label: 'View all',
      onClick: () => onTabClick(Tabs.ViewAll),
    },
    {
      id: Tabs.Overbudget,
      label: 'Overbudget',
      onClick: () => onTabClick(Tabs.Overbudget),
      counter: totalOverbudgetCampaigns,
    },
  ];

  const handleOpenCampaignEditModal = ({ id }: { id: number }) => {
    addParam(MODAL_ID, `${id}`);
  };

  useEffect(() => {
    if (isCampaignCreation) {
      setTitle('Create campaign');
    }

    return removeTitle;
  }, [isCampaignCreation]);

  useEffect(() => {
    const isAddCampaign = !!getParam(CAMPAIGN_CREATION_QUERY_KEY);

    if (isAddCampaign) {
      startCampaignCreation();
      removeParam(CAMPAIGN_CREATION_QUERY_KEY);
    }
  }, [location]);

  if (pageStates.isTableLoading) {
    return <Loader className="mt-20 flex justify-center" />;
  }

  if (isCampaignCreation) {
    return <CreateCampaignWizard onClose={onCreateCampaignCompletion} />;
  }

  if (pageStates.hasErrors) {
    return <PageError />;
  }

  if (pageStates.noItemsInDB) {
    return (
      <InfoBox
        title="👋 Welcome to the Campaigns page!"
        text={`Let's add your first business campaign and start getting new clients.`}
        button={
          <Button
            onClick={startCampaignCreation}
            iconLeftName="add_circle_outline"
          >
            Add campaign
          </Button>
        }
        picture={<NoCampaignsCharacter />}
      />
    );
  }

  return (
    <>
      {IS_ONBOARDING_ENABLE && onboardingData && (
        <OnboardingCampaign
          onboardingData={onboardingData}
          refetchOnboarding={getOnboardingStatusQuery.refetch}
          className="mb-6"
        />
      )}
      <div className="mx-auto my-0 mb-4 flex justify-between">
        <div className="flex">
          <ButtonGroups
            items={buttonGroupsItems}
            selectedId={currentTab}
          />
          {personQuery.data?.data.featureFlags?.holidayScheduleEnabled && (
            <Button
              className="group ml-2"
              onClick={openHolidaySchedule}
              size="medium"
              iconLeftName="beach_access"
              variant="outline"
            >
              Holiday schedule
            </Button>
          )}
        </div>
        <Button
          className="group ml-2"
          onClick={startCampaignCreation}
          size="medium"
          iconLeftName="add_circle_outline"
          {...getDataAutoTestAttributes(['button-add-campaign'])}
        >
          Add campaign
        </Button>
      </div>
      <SideModalFromUrlParam pageStates={pageStates}>
        {(id) => {
          return <CampaignEditModal id={id} />;
        }}
      </SideModalFromUrlParam>
      <SideModalFromUrlParam
        pageStates={pageStates}
        queryKey={HOLYDAY_SCHEDULE_QUERY_KEY}
      >
        {() => <HolidayScheduleModal />}
      </SideModalFromUrlParam>
      {currentTab === Tabs.ViewAll && (
        <ContractorCampaignsViewAll
          isOnboarding={isOnboarding}
          onTableRowClick={handleOpenCampaignEditModal}
        />
      )}
      {currentTab === Tabs.Overbudget && (
        <ContractorCampaignsOverbudget onTableRowClick={handleOpenCampaignEditModal} />
      )}
    </>
  );
}
